/**
 * Workflow Controls Component
 *
 * Bottom navigation bar with action buttons.
 * Matches desktop main_window.py button layout.
 *
 * Buttons:
 * - Previous PDF
 * - Skip (mark as processed without Tebra action)
 * - Connect to Tebra (opens patient match panel)
 * - Next PDF
 * - Close Session
 */

import { Box, Button, Typography } from '@mui/material';
import {
  NavigateBefore,
  NavigateNext,
  SkipNext,
  Link as LinkIcon,
  Close,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../../store/sessionStore';
import { usePDFStore } from '../../store/pdfStore';
import { sessionsApi } from '../../api/sessions';
import { useSnackbar } from '../../contexts/SnackbarContext';

interface WorkflowControlsProps {
  onPrevious: () => void;
  onNext: () => void;
  onConnectToTebra: () => void;
  tebraWorkflowActive: boolean;
}

export const WorkflowControls: React.FC<WorkflowControlsProps> = ({
  onPrevious,
  onNext,
  onConnectToTebra,
  tebraWorkflowActive,
}) => {
  const navigate = useNavigate();
  const { activeSession, customerId, clearSession } = useSessionStore();
  const { currentPDFIndex, totalPDFs, clearPDF } = usePDFStore();
  const { showSuccess, showError } = useSnackbar();

  const handleSkip = () => {
    // Mark current PDF as processed without Tebra action
    onNext();
  };

  const handleCloseSession = async () => {
    if (!activeSession) return;

    const confirmed = window.confirm(
      'Are you sure you want to close this session? All PDFs will be deleted from storage.'
    );

    if (!confirmed) return;

    try {
      const summary = await sessionsApi.closeSession(activeSession.session_id, customerId);

      showSuccess(
        `Session closed! Duration: ${summary.duration_minutes} min | ` +
        `Billable: ${summary.total_pdfs_viewed} | Total Uploaded: ${summary.total_pdfs_processed} | ` +
        `Processed: ${summary.pdfs_processed_count} | ` +
        `New: ${summary.new_records_count} | Updated: ${summary.updated_records_count}`
      );

      clearPDF();
      clearSession();
      navigate('/');
    } catch (error: any) {
      showError('Failed to close session: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'background.paper',
      }}
    >
      {/* Left: Navigation Info */}
      <Box>
        <Typography variant="body2" color="text.secondary">
          PDF {currentPDFIndex + 1} of {totalPDFs || '?'}
        </Typography>
        {activeSession && (
          <Typography variant="caption" color="text.secondary">
            Session: {activeSession.session_id.substring(0, 8)}...
          </Typography>
        )}
      </Box>

      {/* Center: Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<NavigateBefore />}
          onClick={onPrevious}
          disabled={currentPDFIndex === 0}
        >
          Previous
        </Button>

        <Button variant="outlined" startIcon={<SkipNext />} onClick={handleSkip}>
          Skip
        </Button>

        <Button
          variant="contained"
          startIcon={<LinkIcon />}
          onClick={onConnectToTebra}
          color={tebraWorkflowActive ? 'success' : 'primary'}
        >
          {tebraWorkflowActive ? 'Tebra Connected' : 'Connect to Tebra'}
        </Button>

        <Button
          variant="outlined"
          endIcon={<NavigateNext />}
          onClick={onNext}
          disabled={totalPDFs > 0 && currentPDFIndex >= totalPDFs - 1}
        >
          Next
        </Button>

        <Button
          variant="outlined"
          color="error"
          startIcon={<Close />}
          onClick={handleCloseSession}
          sx={{ ml: 4 }}
        >
          Close Session
        </Button>
      </Box>

      {/* Right: Session Counters */}
      {activeSession && (
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="text.secondary">
            Billable (Viewed): {activeSession.counters.total_pdfs_viewed} | Total Uploaded:{' '}
            {activeSession.counters.total_pdfs}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            EMR: {activeSession.counters.new_records_count} New |{' '}
            {activeSession.counters.updated_records_count} Updated
          </Typography>
        </Box>
      )}
    </Box>
  );
};
